import { create, StateCreator } from 'zustand'
import { persist } from 'zustand/middleware'

import { ItemTileProps } from 'lib/components/ItemTile/ItemTile'
import { ImpersonatingUser } from 'lib/components/SiteHeader/SiteHeader'

import { Cart } from '@/types/cart'
import { Order } from '@/types/order'
import { Payment } from '@/types/payment'

import { ProductLineCoverageOption } from '../api/useProductLineApi'

const STORE_NAME = 'cart-store'

export type ShipmentNoteType = 'OUTBOUND' | 'INBOUND'

export interface ShipmentNote {
  note: string
  type: ShipmentNoteType
}

// State

export type CartState = {
  acceptedNotes: NoteAcceptance[]
  cartToken?: string
  cart: Cart
  hasHydrated?: boolean
  order?: Order
  payment: Payment
  selectedProducts: BookingFlowProduct[]
  travelers: TravelerDetailsItemData[]
  noReceipt: boolean
  impersonatingUser?: ImpersonatingUser
  shipmentNotes: ShipmentNote[]
  addUserModalShown: boolean
  isLoading: boolean
  clickId?: string // for tracking marketing click IDs
}

export interface NoteAcceptance {
  noteId: string
  noteType: string
  acceptedAt: string // ISO date string
}

export interface BookingFlowProduct extends OrderProductLabel, PackageAndProtectionItemData {
  selectionDisplayName?: string
  traveler?: string
}

export interface PackageAndProtectionItemData extends PackageAndProtectionItemValues {
  names?: string[]
  packagingTypes?: PackageAndProtectionPackagingType[]
  coverageOptions?: PackageAndProtectionCoverageOption[]
}

export interface PackageAndProtectionItemValues {
  product?: string
  name?: string
  packagingType?: string
  coverageAmountCents?: number
  insuranceId?: string
  insurance?: ProductLineCoverageOption
  additionalInfo?: string
}

export interface PackageAndProtectionPackagingType
  extends Omit<ItemTileProps, 'eyebrow' | 'onClick'> {
  id: string
}

export interface PackageAndProtectionCoverageOption {
  id: string
  price: number
  value: string | number
  title: string
}

export interface TravelerDetailsItemData {
  id?: string
  name?: string
  phone?: string
  email?: string
  sms?: boolean
  userExists?: boolean
  grade?: TravelerDetailsItemGradeOption['value']
}

interface TravelerDetailsItemGradeOption {
  value: string
  label: string
}

export const initState: CartState = {
  acceptedNotes: [],
  cartToken: undefined,
  cart: {
    id: '',
    cartToken: '',
    displayId: '',
  } as Cart,
  payment: {},
  selectedProducts: [],
  travelers: [],
  noReceipt: false,
  shipmentNotes: [],
  impersonatingUser: undefined,
  addUserModalShown: false,
  isLoading: false,
}

// Actions

export type CartActions = {
  clearCart: () => void
  setAcceptedNotes: (notes: NoteAcceptance[]) => void
  setCartToken: (token: string) => void
  setCart: (cart: Cart) => void
  setHasHydrated: (state: boolean) => void
  setOrder: (order: Order) => void
  setPayment: (data: Payment) => void
  setSelectedProducts: (products: BookingFlowProduct[]) => void
  setTravelers: (data: TravelerDetailsItemData[]) => void
  setNoReceipt: (noReceipt: boolean) => void
  setImpersonatingUser: (user?: ImpersonatingUser) => void
  appendShipmentNote: (note: ShipmentNote) => void
  setAddUserModalShown: (state: boolean) => void
  setIsLoading: (isLoading: boolean) => void
  setClickId: (clickId?: string) => void
}

// Store

export type CartStore = CartState & CartActions

const cartStoreCreator: StateCreator<CartStore> = (set) => ({
  ...initState,
  clearCart: () => set(() => initState),
  setAcceptedNotes: (acceptedNotes) => set(() => ({ acceptedNotes })),
  setCartToken: (cartToken) => set({ cartToken }),
  setCart: (cart) =>
    set((state) => {
      const newState = {
        ...state,
        cartToken: cart.cartToken,
        cart: { ...cart },
      } as CartStore
      return newState
    }),
  setHasHydrated: (hasHydrated) => set({ hasHydrated }),
  setOrder: (order) => set(() => ({ order })),
  setPayment: (payment) => set(() => ({ payment })),
  setSelectedProducts: (selectedProducts) => set({ selectedProducts }),
  setTravelers: (travelers) => set(() => ({ travelers })),
  setNoReceipt: (noReceipt) => set(() => ({ noReceipt })),
  setImpersonatingUser: (user) => set(() => ({ impersonatingUser: user })),
  appendShipmentNote: (note) => set((state) => ({ shipmentNotes: [...state.shipmentNotes, note] })),
  setAddUserModalShown: (addUserModalShown) => set({ addUserModalShown }),
  setIsLoading: (isLoading: boolean) => set({ isLoading }),
  setClickId: (clickId) => set({ clickId }),
})

const useCartStore = create<CartStore>()(
  persist<CartStore>(cartStoreCreator, {
    name: STORE_NAME,
    onRehydrateStorage: () => (state) => state?.setHasHydrated(true),
  }) as StateCreator<CartStore>,
)

export function totalFromCart(cart: Cart | null): number {
  let total = 0

  if (!cart?.orderSummary) return total

  for (const leg of cart.orderSummary.legs || []) {
    total += leg.pickupFeeCents || 0

    for (const traveler of leg.travelers || []) {
      for (const shipment of traveler.shipments || []) {
        total += shipment.totalPriceCents || 0
      }
    }
  }

  return total
}

export default useCartStore
